"use client";
import { useD3ClientPage } from "@/hook/useD3ClientPage";
import { IDebates, Stance } from "@/types";
import { Box, Button, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import CelebrationIconButton from "./CelebrationIconButton";
import { useLocale, useTranslations } from "next-intl";
import { safeT } from "@/utils/safeT";
import GoogleTranslateText from "./GoogleTranslateText";

interface VoteButtonsProps {
  debate: IDebates;
  onVote: (debateId: string, lang: string, voteType: Stance) => void;
  disabled: boolean;
  width?: string | number;
}

const VoteButtons = ({ debate, onVote, width = "100%", disabled }: VoteButtonsProps) => {
  const theme = useTheme();
  const t = useTranslations();
  const locale = useLocale();
  const [selectedVote, setSelectedVote] = useState<Stance | null>(null);
  const [celebrate, setCelebrate] = useState<{ [key in Stance]?: boolean }>({}); // 애니메이션 fire 조건

  const proButtonRef = useRef<HTMLButtonElement>(null);
  const conButtonRef = useRef<HTMLButtonElement>(null);

  // 버튼 클릭 처리 (토글)
  const handleButtonClick = (voteType: Stance) => {
    if (selectedVote === voteType) {
      setSelectedVote(null);
    } else {
      setSelectedVote(voteType);
    }
  };

  // 확정 클릭 처리 + 애니메이션 트리거
  const handleConfirm = (voteType: Stance) => {
    onVote(debate.id, debate.lang, voteType);
    setCelebrate((prev) => ({ ...prev, [voteType]: true }));
  };

  // 애니메이션 끝난 후 리셋
  const handleAnimationEnd = (voteType: Stance) => {
    setCelebrate((prev) => ({ ...prev, [voteType]: false }));
    setSelectedVote(null);
  };

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectedVote &&
        proButtonRef.current &&
        conButtonRef.current &&
        !proButtonRef.current.contains(event.target as Node) &&
        !conButtonRef.current.contains(event.target as Node)
      ) {
        setSelectedVote(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedVote]);

  return (
    <Box sx={{ display: "flex", gap: 2, justifyContent: "center", width }}>
      {/* 찬성 버튼 */}
      <Button
        variant="contained"
        size="large"
        onClick={() => handleButtonClick("pros")}
        ref={proButtonRef}
        disabled={disabled}
        sx={{
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          width: "50%",
          display: "flex",
          flexDirection: "column", // ✅ 수직 정렬
          justifyContent: "center",
          alignItems: "center",
          padding: "8px 12px",
          gap: 1,
          textAlign: "center",
          "&.Mui-disabled": {
            backgroundColor: theme.palette.secondary.main,
            color: "rgba(255, 255, 255, 0.6)",
            opacity: 1,
          },
        }}
        aria-label={`${debate.proName} ${safeT(t, "ui.label.vote", {
          defaultMessage: "투표",
        })}`}
      >
        {debate.lang === locale ? (
          <span style={{ flex: 1 }}>{t("ui.message.vote", { option: debate.proName })}</span>
        ) : (
          <GoogleTranslateText
            sourceLang={debate.lang}
            targetLang={locale}
            text={t("ui.message.vote", { option: debate.proName }) ?? ""}
          />
        )}
        {selectedVote === "pros" && (
          <CelebrationIconButton
            onClick={() => {
              handleConfirm("pros");
            }}
            fire={Boolean(celebrate["pros"])}
            onAnimationEnd={() => handleAnimationEnd("pros")}
            particleCount={50}
            maxDistance={200}
            colors={["#2ed573"]}
            aria-label={safeT(t, "ui.label.proConfirmed", {
              defaultMessage: "찬성 확정",
            })}
          />
        )}
      </Button>

      {/* 반대 버튼 */}
      <Button
        variant="contained"
        size="large"
        onClick={() => handleButtonClick("cons")}
        disabled={disabled}
        ref={conButtonRef}
        sx={{
          backgroundColor: theme.palette.tertiary.main,
          color: theme.palette.secondary.contrastText,
          width: "50%",
          display: "flex",
          flexDirection: "column", // ✅ 수직 정렬
          justifyContent: "center",
          alignItems: "center",
          padding: "8px 12px",
          gap: 1,
          textAlign: "center",
          "&.Mui-disabled": {
            backgroundColor: theme.palette.tertiary.main,
            color: "rgba(255, 255, 255, 0.6)",
            opacity: 1,
          },
        }}
        aria-label={`${debate.conName} ${safeT(t, "ui.label.vote", {
          defaultMessage: "투표",
        })}`}
      >
        {debate.lang === locale ? (
          <span style={{ flex: 1 }}>{t("ui.message.vote", { option: debate.conName })}</span>
        ) : (
          <GoogleTranslateText
            sourceLang={debate.lang}
            targetLang={locale}
            text={t("ui.message.vote", { option: debate.conName }) ?? ""}
          />
        )}
        {selectedVote === "cons" && (
          <CelebrationIconButton
            onClick={() => {
              handleConfirm("cons");
            }}
            fire={Boolean(celebrate["cons"])}
            onAnimationEnd={() => handleAnimationEnd("cons")}
            particleCount={50}
            maxDistance={200}
            colors={["#ff5252"]}
            fireAlign="left"
            aria-label={safeT(t, "ui.label.conConfirmed", {
              defaultMessage: "반대 확정",
            })}
          />
        )}
      </Button>
    </Box>
  );
};

export default VoteButtons;
